import { DEFAULT_RADIUS, SKIN_PARAM_RANGES } from '../constants';
import type { ComputedTextStyle } from './hooks/useComputedStyle';
import type {
  RevealEffectConfiguration,
  SwarmEffectConfiguration,
  PerlinEffectConfiguration,
  EffectConfiguration,
  StartPosition,
  ParticleColorConfiguration,
  ParticleOpacityConfiguration,
  ParticleSizeConfiguration,
} from './TextParticles.types';
import { AnimationType } from './TextParticles.types';
import { getDevicePixelRatio } from './utils/canvas-utils';
import { normalize } from './utils/math-utils';
import { lerp } from '@wix/wow-utils';

export const getEffectConfiguration = (
  computedStyle: ComputedTextStyle | undefined,
  text: string,
  isEditor: boolean,
): EffectConfiguration | null => {
  if (!computedStyle) {
    return null;
  }

  const devicePixelRatio = getDevicePixelRatio();
  // For devicePixelRatio=3 (mobile screens) cap the DPR at 1 because it makes the particles look too large relative to text
  const adjustedDPR = devicePixelRatio <= 2 ? devicePixelRatio : 1;

  const effectType = computedStyle.effectType;
  const animationType = computedStyle.animationType;
  const animationDuration = computedStyle.animationDuration * 1000;

  switch (effectType) {
    case 'perlin': {
      const perlinEffectConfiguration: PerlinEffectConfiguration = {
        speed: computedStyle.speed,
        noise: computedStyle.noise,
        amplitude: computedStyle.amplitude,
        effectType,
        animationDuration: isEditor ? 10000 : Infinity,
        text,
        presetId: computedStyle.presetId,
        color: computedStyle.color,
      };
      return perlinEffectConfiguration;
    }
    case 'swarm': {
      const swarmEffectConfiguration: SwarmEffectConfiguration = {
        startPosition: computedStyle.startPosition,
        radius: DEFAULT_RADIUS * adjustedDPR,
        effectType,
        animationDuration,
        text,
        animationType,
        speed: computedStyle.speed,
        turbulence: computedStyle.turbulence,
        presetId: computedStyle.presetId,
        color: computedStyle.color,
      };
      return swarmEffectConfiguration;
    }
    case 'reveal': {
      const particleDuration = computedStyle.particleDuration * 1000;

      const animationDirection =
        animationType === AnimationType.IN
          ? computedStyle.animationDirection
          : (computedStyle.animationDirection + 180) % 360;

      const revealEffectConfiguration: RevealEffectConfiguration = {
        windDirection: computedStyle.windDirection,
        animationDirection,
        particleDuration,
        windSpeed: computedStyle.speed,
        turbulence: computedStyle.turbulence,
        particleColorCount: computedStyle.particleColorCount,
        particleColor1: `rgb(${computedStyle.particleColor1})`,
        particleColor2: `rgb(${computedStyle.particleColor2})`,
        particleColor3: `rgb(${computedStyle.particleColor3})`,
        particleColor4: `rgb(${computedStyle.particleColor4})`,
        radius: DEFAULT_RADIUS * adjustedDPR,
        effectType,
        animationType,
        animationDuration,
        text,
        particleStartSize: computedStyle.particleStartSize,
        particleEndSize: computedStyle.particleEndSize,
        particleStartOpacity: computedStyle.particleStartOpacity,
        particleEndOpacity: computedStyle.particleEndOpacity,
        presetId: computedStyle.presetId,
        color: computedStyle.color,
      };
      return revealEffectConfiguration;
    }

    default:
      const _exhaustiveCheck: never = effectType;
      throw new Error(`Unsupported effect type ${_exhaustiveCheck}`);
  }
};

export const hasAnimationType = (
  config: EffectConfiguration | null,
): config is EffectConfiguration & { animationType: AnimationType } => {
  return config !== null && 'animationType' in config;
};

export const isPerlinEffect = (
  effectConfiguration: EffectConfiguration,
): effectConfiguration is PerlinEffectConfiguration =>
  effectConfiguration.effectType === 'perlin';

export const isSwarmEffect = (
  effectConfiguration: EffectConfiguration,
): effectConfiguration is SwarmEffectConfiguration =>
  effectConfiguration.effectType === 'swarm';

export const isRevealEffect = (
  effectConfiguration: EffectConfiguration,
): effectConfiguration is RevealEffectConfiguration =>
  effectConfiguration.effectType === 'reveal';

export const hasStartPosition = (
  effectConfiguration: EffectConfiguration | null,
): effectConfiguration is EffectConfiguration & {
  startPosition: StartPosition;
} => effectConfiguration !== null && 'startPosition' in effectConfiguration;

export const hasParticleSize = (
  effectConfiguration: EffectConfiguration | null,
): effectConfiguration is EffectConfiguration & ParticleSizeConfiguration =>
  effectConfiguration !== null &&
  'particleStartSize' in effectConfiguration &&
  'particleEndSize' in effectConfiguration;

export const hasParticleOpacity = (
  effectConfiguration: EffectConfiguration | null,
): effectConfiguration is EffectConfiguration & ParticleOpacityConfiguration =>
  effectConfiguration !== null &&
  'particleStartOpacity' in effectConfiguration &&
  'particleEndOpacity' in effectConfiguration;

export const getColorProgression = (
  particleColorConfig: ParticleColorConfiguration,
) =>
  [
    particleColorConfig.particleColor1,
    particleColorConfig.particleColor2,
    particleColorConfig.particleColor3,
    particleColorConfig.particleColor4,
  ].slice(0, particleColorConfig.particleColorCount);

export const getNormalizedPerlinParams = (
  effectConfiguration: PerlinEffectConfiguration,
): { noiseScale: number; driftSpeed: number; displacementStrength: number } => {
  const { speed, noise, amplitude } = effectConfiguration;
  const driftSpeedRange = { min: 0.0002, max: 0.001 };
  const noiseRange = { min: 150, max: 1500 }; // distortion, smaller is more distortion
  const amplitudeRange = { min: 10, max: 100 };

  const driftSpeed = lerp(
    driftSpeedRange.min,
    driftSpeedRange.max,
    normalize(speed, SKIN_PARAM_RANGES.speed.min, SKIN_PARAM_RANGES.speed.max),
  );

  const noiseScale = lerp(
    noiseRange.max,
    noiseRange.min,
    normalize(noise, SKIN_PARAM_RANGES.noise.min, SKIN_PARAM_RANGES.noise.max),
  );

  const displacementStrength = lerp(
    amplitudeRange.min,
    amplitudeRange.max,
    normalize(
      amplitude,
      SKIN_PARAM_RANGES.amplitude.min,
      SKIN_PARAM_RANGES.amplitude.max,
    ),
  );
  return { noiseScale, driftSpeed, displacementStrength };
};

export const getParticleRadius = (effectConfiguration: EffectConfiguration) => {
  if ('radius' in effectConfiguration) {
    return effectConfiguration.radius;
  }

  return DEFAULT_RADIUS;
};

export const getAnimationType = (
  effectConfiguration: EffectConfiguration,
): AnimationType => {
  if ('animationType' in effectConfiguration) {
    return effectConfiguration.animationType;
  }
  return AnimationType.IN;
};

export const getTextContentPadding = (
  effectConfiguration: EffectConfiguration,
) => {
  if (isPerlinEffect(effectConfiguration)) {
    return Math.ceil(
      getNormalizedPerlinParams(effectConfiguration).displacementStrength,
    );
  }
  return 0;
};
